// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A PEGA DA BARRA — esticar/encurtar uma viagem arrastando a sua ponta (§11 · M)
// ============================================================================
//
// 📄 `docs/superpowers/specs/2026-09-11-agilidade-do-planeador-design.md`, §11 · M: «os dois
//    `onPointerDown` do `Mapa.tsx` são as MEDIDAS das colunas e das linhas (D-085) — a ponta de
//    uma barra de viagem não tem gesto nenhum». Este ficheiro é a aritmética desse gesto novo:
//    detectar que se agarrou a ponta esquerda ou direita de uma viagem, e calcular o intervalo
//    que resulta de a arrastar. **Não decide nada sobre o plano** — quem escreve continua a ser
//    `/api/pedidos/alterar`, pela mesma janela `DatasDoPedido` que já existe (regra 8).
//
// ⚠️ SEM DOM E SEM REACT, de propósito — como `viagens-da-linha.ts` e `segmentos.ts`. O
//    `Mapa.tsx` lê as posições do rato e as medidas da célula; isto só faz a conta.
//
// ⚠️⚠️ **O DIA DE ROTAÇÃO PODE TER DUAS VIAGENS NO MESMO SÍTIO.** Um dia de rotação é ao mesmo
//    tempo o ÚLTIMO dia da viagem que chega e o PRIMEIRO dia da que parte (`extentoDaCorrida` +
//    `extentoDaViagemQueParte`, em `Mapa.tsx`). Isto não é ambíguo em geral — o fim de uma cai na
//    borda DIREITA da célula e o início da outra na borda ESQUERDA, que são pixéis diferentes —
//    mas fica genuinamente ambíguo quando uma das duas viagens tem exactamente UM dia: aí o seu
//    início e o seu fim caem na MESMA borda que a outra viagem também usa. Nesse caso concreto
//    `pegaDaBarra` devolve `'ambigua'`, e quem chama recusa o arrasto e di-lo — nunca escolhe ao
//    acaso qual das duas viagens o planeador queria (instrução da tarefa).
// ============================================================================

/** Os dois dias que uma corrida ocupa nesta linha, inclusive dos dois lados. */
export interface ExtentoDeCorrida {
  readonly dia0: number;
  readonly dia1: number;
}

export type PontaDaBarra = 'inicio' | 'fim';

export type ResultadoDaPega<T extends ExtentoDeCorrida> =
  | { readonly tipo: 'pega'; readonly ponta: PontaDaBarra; readonly corrida: T }
  | { readonly tipo: 'ambigua' }
  | { readonly tipo: 'nenhuma' };

/**
 * A pega sob o ponteiro, dentro da célula do dia `dia` — ou `'nenhuma'` se o ponteiro não está
 * perto de nenhuma ponta, ou `'ambigua'` se estiver perto de uma ponta que serve a MAIS DE UMA
 * candidata (ver o cabeçalho do ficheiro).
 *
 * ⚠️ **Só a banda do ITINERÁRIO tem pega** (`yNaCelula < alturaBandaItinerario`) — é onde a barra
 *    de viagem vive; agarrar a banda da localidade ou a do motorista não estica nada, e teria
 *    tapado o clique normal nessas bandas.
 *
 * ⚠️ **A zona é pequena de propósito** (`larguraTira`, a MESMA medida das tiras de D-085): maior
 *    do que isto roubava o meio da célula ao arrasto que já existe (mover a viagem inteira,
 *    `draggable` em `Mapa.tsx`).
 */
export function pegaDaBarra<T extends ExtentoDeCorrida>(params: {
  readonly dia: number;
  readonly xNaCelula: number;
  readonly yNaCelula: number;
  readonly larguraCelula: number;
  readonly alturaBandaItinerario: number;
  readonly larguraTira: number;
  readonly candidatas: readonly T[];
}): ResultadoDaPega<T> {
  if (params.yNaCelula < 0 || params.yNaCelula >= params.alturaBandaItinerario) {
    return { tipo: 'nenhuma' };
  }
  const pertoEsquerda = params.xNaCelula < params.larguraTira;
  const pertoDireita = params.xNaCelula > params.larguraCelula - params.larguraTira;
  if (!pertoEsquerda && !pertoDireita) return { tipo: 'nenhuma' };

  const naEsquerda = params.candidatas.filter((c) => c.dia0 === params.dia);
  const naDireita = params.candidatas.filter((c) => c.dia1 === params.dia);

  // ⚠️⚠️ Duas candidatas na MESMA borda não se distinguem pela posição do rato — recusa-se em
  //    vez de escolher uma ao acaso (ver o cabeçalho).
  if (pertoEsquerda && naEsquerda.length > 1) return { tipo: 'ambigua' };
  if (pertoDireita && naDireita.length > 1) return { tipo: 'ambigua' };

  if (pertoEsquerda && naEsquerda.length === 1) {
    return { tipo: 'pega', ponta: 'inicio', corrida: naEsquerda[0]! };
  }
  if (pertoDireita && naDireita.length === 1) {
    return { tipo: 'pega', ponta: 'fim', corrida: naDireita[0]! };
  }
  return { tipo: 'nenhuma' };
}

/**
 * O intervalo depois de arrastar a `ponta` da `corrida` até ao dia `diaAlvo`.
 *
 * ⚠️ **Nunca inverte.** Arrastar o início para além do fim (ou o fim para aquém do início) fica
 *    preso ao outro extremo — um serviço de um dia só, nunca um serviço com `dia0 > dia1`. É a
 *    mesma disciplina do `DatasDoPedido.tsx` (`invalido = spanNovo < 1`), aplicada antes de a
 *    janela sequer se abrir.
 */
export function novoIntervaloAposArrasto<T extends ExtentoDeCorrida>(
  corrida: T,
  ponta: PontaDaBarra,
  diaAlvo: number,
): ExtentoDeCorrida {
  if (ponta === 'inicio') {
    return { dia0: Math.min(diaAlvo, corrida.dia1), dia1: corrida.dia1 };
  }
  return { dia0: corrida.dia0, dia1: Math.max(diaAlvo, corrida.dia0) };
}
